import { useState } from 'react';
import { usePageRestore } from '../../lib/browser/usePageRestore';
import { isEntraLaptopMode } from '../../lib/config/entraLaptopMode';

/**
 * The busy state for a button that starts the Microsoft consent redirect — *Try again* and *Reconnect*
 * on the mailbox screen and the Email screen (`connect/NextStepAction.tsx`), the connect-failed banner's
 * *Try again* and the lapse notice's *Reconnect* (BUG-024). One rule, held here so the buttons cannot
 * drift apart:
 *
 * - **Busy on press.** `run(start)` sets `busy` before it calls `start`.
 * - **Cleared where the page stays.** Under `import.meta.env.DEV`, unless laptop mode is on
 *   (`isEntraLaptopMode()`, F5-i), `start` is the stub connect, which re-reads in place, so `busy`
 *   clears when it resolves. When `start` rejects, the redirect never began, so `busy` clears and
 *   `run` resolves `false` for the caller to show its error.
 * - **Kept after a real redirect starts.** In a deployed bundle, and under `pnpm dev` in laptop mode,
 *   `start` has just called `location.assign`, which returns before the browser leaves. Clearing `busy`
 *   there would let a second press start a second consent redirect, so it stays set. The condition
 *   keeps the literal `import.meta.env.DEV` first, the same as `MailboxConnectProvider`'s start, so it matches
 *   the branch that chose the stub.
 * - **Cleared when the page comes back from the back-forward cache.** A user who reaches the consent
 *   page and presses Back can get this page restored with its React state intact; a `pageshow` event
 *   with `persisted: true` is that restore, and it clears `busy` so the button works again without a
 *   reload.
 *
 * There is deliberately no timeout: a slow redirect must not re-enable the button while the browser
 * is still leaving.
 */
export interface ConsentRedirectBusy {
    readonly busy: boolean;
    /**
     * What the last `start` rejected with, for the caller's error panel to show its trace id
     * (`traceIdOf`); `undefined` once a new `run` begins and while nothing has failed.
     */
    readonly error: unknown;
    /** Start the redirect. Resolves `true` when it began, `false` when `start` rejected. */
    readonly run: (start: () => Promise<void>) => Promise<boolean>;
}

export function useConsentRedirectBusy(): ConsentRedirectBusy {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>(undefined);

    usePageRestore(() => setBusy(false));

    async function run(start: () => Promise<void>): Promise<boolean> {
        setBusy(true);
        setError(undefined);
        try {
            await start();
            if (import.meta.env.DEV && !isEntraLaptopMode()) {
                setBusy(false);
            }
            return true;
        } catch (caught) {
            setBusy(false);
            setError(caught);
            return false;
        }
    }

    return { busy, error, run };
}
